<script lang="ts" setup>
import { useBaseView, useNotFound } from "@/composables";
import TruckViewSidebar from "@/views/fleets/components/TruckViewSidebar.vue";
import SidebarLayout from "@/components/layout/SidebarLayout.vue";
import SidebarToggle from "@/components/buttons/SidebarToggle.vue";
import NotFound from "@/views/notFound/NotFound.vue";
import TruckAdditionalActions from "@/views/fleets/components/TruckAdditionalActions.vue";
import { computed, onMounted, ref, watch } from "vue";
import type { TruckDto, TruckWithCoordinates } from "@/api/trucks/dto/truck.dto";
import { useRoute, useRouter } from "vue-router";
import { API } from "@/api/api";
import OmniTabs from "@/components/omni/OmniTabs.vue";
import { TruckViewTab, TruckViewTabHash } from "@/constants/truck";
import TruckActiveRoutes from "@/views/fleets/components/TruckActiveRoutes.vue";
import TruckGeneralInfo from "@/views/fleets/components/TruckGeneralInfo.vue";
import TruckReservations from "@/views/fleets/components/TruckReservations.vue";
import OmniTooltip from "@/components/omni/OmniTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { useI18n } from "vue-i18n";
import TruckExclusiveIcon from "@/components/icons/TruckExclusiveIcon.vue";

const api = new API();
const router = useRouter();

const route = useRoute();
const { entityNotFound, validateEntityResponse } = useNotFound();
const { hasAccess } = useBaseView();
const { t } = useI18n();

const truck = ref<TruckWithCoordinates | null>(null);
const loading = ref<boolean>(true);
const tab = ref<TruckViewTab>(TruckViewTab.general);

const editLink = computed(() => {
  const name = route.name;

  if (typeof name === "string" && truck.value) {
    return {
      name: name.replace("view", "edit"),
      params: { id: truck.value.id },
    };
  }

  return null;
});

const hasAccessToEdit = computed(() => {
  return hasAccess(DispatcherPermissionValue.TrucksEdit);
});

function isSidebarOpenDefault() {
  const windowWidth = window.innerWidth;
  return windowWidth > 1250;
}

async function loadTruck() {
  loading.value = true;
  const resp = await api.trucks.findTruckById(String(route.params.id));
  if (!validateEntityResponse<TruckDto>(resp) || !resp.data) {
    return;
  }

  const truckWithCoordinators: TruckWithCoordinates = { coordinators: [], ...resp.data };

  if (!truckWithCoordinators.ownerId) {
    return;
  }

  // load truck owner coordinators
  const coordinatorsResp = await api.users.findOwnerCoordinators(truckWithCoordinators.ownerId);
  if (coordinatorsResp.success && coordinatorsResp.data) {
    truckWithCoordinators.coordinators = coordinatorsResp.data.filter((c) => c.fullAccess);
  }

  truck.value = truckWithCoordinators;
  loading.value = false;
}

function setTabByDefault() {
  const hash = route.hash.replace("#", "");

  tab.value =
    Object.values(TruckViewTab).find((value) => {
      return TruckViewTabHash[value] === hash;
    }) ?? TruckViewTab.general;
}

watch(tab, (value) => {
  const hash = TruckViewTabHash[value];

  router.replace({ hash: hash ? `#${hash}` : "" });
});

watch(
  () => route.hash,
  () => {
    setTabByDefault();
  },
);

onMounted(() => {
  loadTruck();
  setTabByDefault();
});
</script>

<template>
  <v-container v-if="!entityNotFound" :fluid="true" class="pt-6 pb-10 pl-6 pr-6 height-100">
    <sidebar-layout v-if="!loading && truck" :sidebar-opened="isSidebarOpenDefault()">
      <div class="w-100">
        <div class="d-flex justify-space-between align-center flex-wrap mb-4">
          <div class="d-flex">
            <div class="text-grey-darken-4 font-weight-medium text-h6" data-qa="truck-number">
              Truck {{ truck.number }}
            </div>

            <div v-if="truck.exclusive" class="d-flex align-center ml-6">
              <truck-exclusive-icon size="18" />

              <span class="text-amber-darken-2 font-weight-500">{{ t("general.exclusive") }}</span>
            </div>

            <v-chip v-if="truck.archived !== null" :label="true" class="ml-6" color="grey-darken-2" variant="flat">
              Archived
            </v-chip>
          </div>
          <div class="d-flex">
            <omni-tooltip :disabled="hasAccessToEdit">
              <template #activator>
                <v-btn
                  v-if="truck.id && editLink"
                  :class="{ 'btn--disabled': truck.archived }"
                  :disabled="!!truck.archived || !hasAccessToEdit"
                  :to="editLink"
                  class="text-uppercase font-weight-600 mr-2"
                  color="primary"
                  variant="flat"
                >
                  <v-icon class="mr-2" size="20"> mdi-pencil-outline</v-icon>
                  Edit Truck
                </v-btn>
              </template>

              {{ t("permission.edit-truck") }}
            </omni-tooltip>
            <sidebar-toggle class="mr-2"></sidebar-toggle>
            <truck-additional-actions :truck="truck" @change="loadTruck"></truck-additional-actions>
          </div>
        </div>

        <v-card class="bg-transparent" variant="flat">
          <omni-tabs v-model="tab" class="rounded">
            <v-tab :base-color="tab === TruckViewTab.general ? 'primary' : ''" :value="TruckViewTab.general">
              {{ t("general.general") }}
            </v-tab>
            <v-tab :base-color="tab === TruckViewTab.activeRoutes ? 'primary' : ''" :value="TruckViewTab.activeRoutes">
              {{ t("trucks.active-routes") }}
            </v-tab>
            <v-tab
              :base-color="tab === TruckViewTab.completedRoutes ? 'primary' : ''"
              :value="TruckViewTab.completedRoutes"
            >
              {{ t("trucks.completed-routes") }}
            </v-tab>
            <v-tab :base-color="tab === TruckViewTab.reservations ? 'primary' : ''" :value="TruckViewTab.reservations">
              {{ t("trucks.reservations") }}
            </v-tab>
          </omni-tabs>

          <v-window v-model="tab" :touch="false" class="mt-4">
            <v-window-item :value="TruckViewTab.general">
              <truck-general-info :truck="truck" />
            </v-window-item>
            <v-window-item :value="TruckViewTab.activeRoutes">
              <truck-active-routes />
            </v-window-item>
            <v-window-item :value="TruckViewTab.completedRoutes">Completed routes</v-window-item>
            <v-window-item :value="TruckViewTab.reservations">
              <truck-reservations :truck="truck" />
            </v-window-item>
          </v-window>
        </v-card>
      </div>
      <template #sidebar>
        <truck-view-sidebar :truck="truck" @update:truck="loadTruck"></truck-view-sidebar>
      </template>
    </sidebar-layout>
  </v-container>
  <not-found v-else icon="mdi-truck-remove-outline" text="Truck not found"></not-found>
</template>

<style lang="scss" scoped></style>
